<template>
  <div>
    <div v-if="trustedPerson===null">
      <v-btn small depressed @click="openSelect()">
        <v-icon small class="pr-2">mdi-magnify</v-icon>
        Поиск
      </v-btn>
    </div>

    <div v-else>
      <div class="title font-weight-regular d-flex align-center mb-1">
        <span>{{ fullName(trustedPerson) }}</span>
      </div>

      <div class="font-weight-regular">
        <call-phone v-if="!$tools.isEmpty(trustedPerson.phone)" :phone="trustedPerson.phone" class="my-2"/>

        <div v-if="!$tools.isEmpty(trustedPerson.relation)" class="my-2">
          <v-icon small class="mr-2">mdi-account-heart-outline</v-icon>{{ trustedPerson.relation }}
        </div>

        <div v-if="documentView(trustedPerson)" class="my-2">
          <v-icon small class="mr-2">mdi-file-document-outline</v-icon>{{ documentView(trustedPerson) }}
        </div>

        <div class="mt-2">
          <v-btn small depressed class="mr-2 mb-2" @click="openSelect()">Изменить выбор</v-btn>
          <v-btn small depressed class="mr-2 mb-2" @click="openEdit(trustedPerson)">Редактировать</v-btn>
          <btn-small-accept
            name="Убрать из визита"
            text="Убрать доверенное лицо из визита?"
            btnClass="mb-2 d-inline-flex"
            @accept="deleteTrustedPersonForVisit()"
          />
        </div>
      </div>
    </div>

    <dialog-view
        :open="mainState.isModalOpen"
        :title="dialogTitle"
        @cancel="close()"
    >
      <template v-slot:content>
        <div v-if="mainState.isLoading" class="py-4 text-center">
          <v-progress-circular indeterminate color="primary"/>
        </div>

        <v-form v-show="mainState.isInit" ref="trustedPersonForm" class="pt-3">
          <v-row>
            <v-col cols="12">
              <user-autocomplete-single
                  name="Пользователь"
                  :select.sync="form.trustedUser"
              />
            </v-col>
            <v-col cols="12">
              <alert-view
                  icon="mdi-information-outline"
                  text="Если человека нет в базе, сначала создайте его как обычного пользователя (клиента), затем выберите здесь."
              />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="form.relation" label="Кем приходится" outlined dense hide-details="auto"/>
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="form.documentNumber" label="Номер документа" outlined dense hide-details="auto"/>
            </v-col>
            <v-col cols="12">
              <simple-date-view :date.sync="form.documentDate" title="Дата документа"/>
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="form.comment" label="Комментарий" outlined dense hide-details="auto" rows="2"/>
            </v-col>
          </v-row>
        </v-form>
      </template>

      <template v-slot:actions>
        <v-btn text @click="saveTrustedPerson()" :loading="mainState.isLoading" color="primary">Сохранить</v-btn>
      </template>
    </dialog-view>
  </div>
</template>

<script>
import State from "@/plugins/state";
import axios from "axios";
import Api from "@/Api";
import moment from "moment";
import AlertView from "@/components/AlertView";
import DialogView from "@/components/DialogView";
import CallPhone from "@/components/CallPhone.vue";
import SimpleDateView from "@/components/SimpleDateView";
import BtnSmallAccept from "../../componentsV2/base/BtnSmallAccept.vue";
import UserAutocompleteSingle from "@/componentsV2/custom/UserAutocompleteSingle.vue";

export default {
  components: {
    BtnSmallAccept,
    AlertView,
    DialogView,
    CallPhone,
    SimpleDateView,
    UserAutocompleteSingle
  },
  props: ['visitId', 'userId', 'trustedPerson'],
  data: () => ({
    mainState: new State(),
    mode: 'select',
    form: {}
  }),
  computed: {
    dialogTitle() {
      if (this.mode === 'edit') return 'Редактировать доверенное лицо';
      return 'Выбрать доверенное лицо';
    }
  },
  methods: {
    emptyForm() {
      this.form = {
        trustedUser: null,
        relation: null,
        documentNumber: null,
        documentDate: null,
        comment: null
      };
    },
    openSelect() {
      this.mode = 'select';
      this.emptyForm();
      this.mainState.modalOpen();
      this.mainState.stateInit();
      this.$nextTick(() => {
        if (this.$refs.trustedPersonForm) this.$refs.trustedPersonForm.resetValidation();
      });
    },
    openEdit(trustedPerson) {
      this.mode = 'edit';
      this.form = {
        trustedUser: this.trustedUserFromResponse(trustedPerson),
        relation: trustedPerson.relation,
        documentNumber: trustedPerson.documentNumber,
        documentDate: trustedPerson.documentDate,
        comment: trustedPerson.comment
      };
      this.mainState.modalOpen();
      this.mainState.stateInit();
      this.$nextTick(() => {
        if (this.$refs.trustedPersonForm) this.$refs.trustedPersonForm.resetValidation();
      });
    },
    saveTrustedPerson() {
      if (!this.form.trustedUser || !this.form.trustedUser.id) {
        Api.responseError({response: {data: {message: 'Выберите доверенное лицо'}}});
        return;
      }

      this.mainState.stateLoading();
      const data = this.prepareFormData();

      axios.post(Api.host + '/visit/' + this.visitId + '/trustedPerson/', data)
          .then((r) => {
            this.$emit(this.mode === 'edit' ? 'update' : 'created', this.responseTrustedPerson(r.data, data));
            this.mainState.modalClose();
          })
          .catch((e) => {
            this.mainState.stateError();
            Api.responseError(e);
          });
    },
    deleteTrustedPersonForVisit() {
      axios.delete(Api.host+'/visit/'+this.visitId+'/trustedPerson/')
        .then(() => {
          this.$emit('delete');
        })
        .catch(e => {
          Api.responseError(e);
        });
    },
    responseTrustedPerson(data, fallback) {
      if (data && data.trustedPerson) return data.trustedPerson;
      if (data && data.id) return data;
      return this.trustedPersonFallback(fallback);
    },
    prepareFormData() {
      return {
        trustedUserId: this.form.trustedUser.id,
        relation: this.form.relation?.trim(),
        documentNumber: this.form.documentNumber?.trim(),
        documentDate: this.dateForApi(this.form.documentDate),
        comment: this.form.comment?.trim()
      };
    },
    trustedPersonFallback(source) {
      return {
        ...this.form.trustedUser,
        relation: source.relation,
        documentNumber: source.documentNumber,
        documentDate: source.documentDate,
        comment: source.comment
      };
    },
    trustedUserFromResponse(trustedPerson) {
      return {
        id: trustedPerson.id,
        firstName: trustedPerson.firstName,
        lastName: trustedPerson.lastName,
        secondName: trustedPerson.secondName,
        phone: trustedPerson.phone,
        type: trustedPerson.type,
        userProfile: trustedPerson.userProfile || {
          birth: trustedPerson.birth,
          gender: trustedPerson.gender,
          email: trustedPerson.email,
          phone1: trustedPerson.phone1
        },
        name: this.fullName(trustedPerson)
      };
    },
    dateForApi(value) {
      if (!value) return null;
      const date = moment(value);
      return date.isValid() ? date.format('YYYY-MM-DD') : value;
    },
    fullName(trustedPerson) {
      return [trustedPerson.lastName, trustedPerson.firstName, trustedPerson.secondName].filter(Boolean).join(' ');
    },
    documentView(trustedPerson) {
      const parts = [];
      if (!this.$tools.isEmpty(trustedPerson.documentNumber)) parts.push(trustedPerson.documentNumber);
      if (!this.$tools.isEmpty(trustedPerson.documentDate)) parts.push(moment(trustedPerson.documentDate).format('DD.MM.YYYY'));
      return parts.join(' от ');
    },
    close() {
      if (this.mainState.isInit) this.mainState.modalClose();
      else this.mainState.stateInit();
    }
  }
}
</script>